import { Injectable } from '@angular/core'
import { getAuth, onAuthStateChanged } from '@angular/fire/auth'
import { environment } from 'src/environments/environment'
import { FirebaseService } from './firebase.service'
import { LocalStorageService } from './local-storage-service.service'

interface Org {
  id: string,
  name: string
}

@Injectable({
  providedIn: 'root'
})

export class OrganizationService {

  verbose: boolean = false
  // databaseRootUrl: string = `https://${environment.firebaseConfig.iamId}.firebaseio.com`
  
  // allows for mulitple firebase database urls based on environment, for example to support different regions
  databaseRootUrl: string =
    environment.firebaseRegion === 'europe-west1'
      ? `https://${environment.firebaseConfig.iamId}.europe-west1.firebasedatabase.app`
      : environment.firebaseRegion === 'asia-southeast1' 
        ? `https://${environment.firebaseConfig.iamId}.asia-southeast1.firebasedatabase.app`
        : `https://${environment.firebaseConfig.iamId}.firebaseio.com`

  organizationData: { [key: string]: any } = {}
  memberOrgData: { [key: string]: any } = {}
  orgMemberData: { [key: string]: any } = {}
  managerOrgData: { [key: string]: any } = {}
  all: Array<Org> = []
  lookup: { [key: string]: Org } = {}

  constructor(
    private fb: FirebaseService,
    private localStorage: LocalStorageService
  ) {
    console.log('[OrganizationService] databaseRootUrl:', this.databaseRootUrl)
    this.init()
  }

  init = async (): Promise<void> => {

    await this.waitForFirebaseAuth()

    const dataPromises = Promise.all<Promise<object>>([
      this.getOrgData(),
      this.getMemberOrgData(),
      this.getOrgMemberData(),
      this.getManagerOrgData()
    ])

    try {
      const value = await dataPromises;
      this.organizationData = value[0];
      this.memberOrgData = value[1];
      this.orgMemberData = value[2];
      this.managerOrgData = value[3];
    } catch (error) {
      if (this.verbose) { console.error('[ OrganizationService - getOrgData() - PROMISE REJECTED ]', error); }
    }
  }

  waitForFirebaseAuth = (): Promise<void> => {
    const auth = getAuth()

    if (auth.currentUser) {
      return Promise.resolve()
    }

    return new Promise<void>((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        (user) => {
          if (user) {
            unsubscribe()
            resolve()
          }
        },
        (error) => {
          unsubscribe()
          reject(error)
        }
      )
    })
  }

  // ------------------------------------------------------------------------

  getOrgData = (): Promise<object> => {

    return new Promise<object>((resolve, reject) => {
      this.fb
        .db(this.databaseRootUrl)
        .object(environment.firebasePaths.state + 'organizations/byId')
        .subscribe((organizations) => {
          resolve(organizations);
        });
    })
  }

  // ------------------------------------------------------------------------

  getMemberOrgData = (): Promise<object> => {

    return new Promise<object>((resolve, reject) => {
      this.fb
        .db(this.databaseRootUrl)
        .object(environment.firebasePaths.state + 'organizationMemberAssociation/memberOrganizations')
        .subscribe((memberOrganizationsAssociation) => {
          // if(this.verbose) { console.log('%c[ memberOrganizationsAssociation ]', 'background-color: whitesmoke; color: black', memberOrganizationsAssociation) }
          resolve(memberOrganizationsAssociation);
        });
    })
  }

  // ------------------------------------------------------------------------

  getOrgMemberData = (): Promise<object> => {

    return new Promise<object>((resolve, reject) => {
      this.fb
        .db(this.databaseRootUrl)
        .object(environment.firebasePaths.state + 'organizationMemberAssociation/organizationMembers')
        .subscribe((organizationMemberAssociation) => {
          resolve(organizationMemberAssociation);
        });
    })
  }

  // ------------------------------------------------------------------------

  getManagerOrgData = (): Promise<object> => {

    return new Promise<object>((resolve, reject) => {
      this.fb
        .db(this.databaseRootUrl)
        .object(environment.firebasePaths.state + 'managerOrganizationAssociation/managerOrganizations')
        .subscribe((managerOrganizationAssociation) => {
          resolve(managerOrganizationAssociation);
        });
    })
  }

}
